import { api } from "./base_service";
import { apiEndpoints } from "@/src/config/api/endpoints";

/** The call-type the ref pins for dashboard aggregates. */
const API_CALL_TYPE = "onboarding";

interface CountRaw {
  count?: number;
}

// --- native date helpers (yyyy-MM-dd, local) -------------------------------
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}

/**
 * First day of `d`'s month. Monthly windows MUST anchor to this: carrying a
 * day-of-month into addMonths (setMonth) overflows on the 29th–31st (e.g. Mar 31
 * → "Feb 31" rolls into March), which would duplicate/skip months in a 12-month
 * fan-out. Month-start has no such ambiguity.
 */
function monthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * `GET /searches/count/?from_timestamp&to_timestamp&api_call_type` — the request
 * count in a window. Returns 0 on any failure (graceful empty, mirrors the ref's
 * `response?.data.count || 0`).
 */
export async function getRequestsCount(
  fromTimestamp: string,
  toTimestamp: string,
  apiCallType: string = API_CALL_TYPE,
): Promise<number> {
  try {
    const data = await api.get<CountRaw>(apiEndpoints.dashboard().counts(), {
      params: {
        from_timestamp: fromTimestamp,
        to_timestamp: toTimestamp,
        api_call_type: apiCallType,
      },
    });
    return data?.count ?? 0;
  } catch {
    return 0;
  }
}

/**
 * % change of `currentCount` vs `previousCount`, to one decimal (string, like
 * the ref). 0→0 is "0.0"; 0→n is "100.0".
 */
export function percentageDifference(previousCount: number, currentCount: number): string {
  if (previousCount === 0) return currentCount === 0 ? "0.0" : "100.0";
  return (((currentCount - previousCount) / previousCount) * 100).toFixed(1);
}

/** Daily counts for the last 30 days, keyed `yyyy-MM-dd` (ref: every day of last month). */
export async function getDailyCountsLast30(
  now: Date = new Date(),
): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  for (let i = 1; i <= 30; i++) {
    const from = addDays(now, -(30 - i));
    const to = addDays(from, 1);
    out[ymd(from)] = await getRequestsCount(ymd(from), ymd(to));
  }
  return out;
}

/** Monthly counts for the last 12 months, keyed `yyyy-MM-dd` (ref: every month of last year). */
export async function getMonthlyCountsLast12(
  now: Date = new Date(),
): Promise<Record<string, number>> {
  const base = monthStart(now);
  const out: Record<string, number> = {};
  for (let i = 1; i <= 12; i++) {
    const from = addMonths(base, -(12 - i));
    const to = addMonths(from, 1);
    out[ymd(from)] = await getRequestsCount(ymd(from), ymd(to));
  }
  return out;
}

/** % change of the current 30-day count vs the prior 30-day window (ref logic). */
export async function getThirtyDayPercentage(
  currentCount: number,
  now: Date = new Date(),
): Promise<string> {
  const previous = await getRequestsCount(ymd(addDays(now, -60)), ymd(addDays(now, -30)));
  return percentageDifference(previous, currentCount);
}

/** % change of the current 12-month count vs the prior 12-month window (ref logic). */
export async function getTwelveMonthPercentage(
  currentCount: number,
  now: Date = new Date(),
): Promise<string> {
  const base = monthStart(now);
  const previous = await getRequestsCount(ymd(addMonths(base, -24)), ymd(addMonths(base, -12)));
  return percentageDifference(previous, currentCount);
}

/** A single point of the screening trend: the window start (`yyyy-MM-dd`) + its count. */
export interface ScreeningCountPoint {
  key: string;
  count: number;
}

/** The dashboard's screening widgets in one shape: card totals + % deltas + chart series. */
export interface ScreeningCounts {
  /** Daily request counts for the last 30 days, oldest → newest. */
  daily30: ScreeningCountPoint[];
  /** Monthly request counts for the last 12 months, oldest → newest. */
  monthly12: ScreeningCountPoint[];
  /** Sum of the last-30-day daily counts (the "Last 30 Days" stat-card value). */
  total30d: number;
  /** Sum of the last-12-month monthly counts (the "Last 12 Months" stat-card value). */
  total12m: number;
  /** % change of `total30d` vs the prior 30-day window (string, one decimal). */
  pct30d: string;
  /** % change of `total12m` vs the prior 12-month window (string, one decimal). */
  pct12m: string;
}

/**
 * One-shot aggregate for the dashboard's screening widgets: the 30-day + 12-month
 * request totals (each with its % change vs the prior window) plus the daily and
 * monthly series the charts plot.
 *
 * Every window is its own `GET /searches/count/`, but they are fired in PARALLEL
 * here (the granular `getDailyCountsLast30` / `getMonthlyCountsLast12` helpers run
 * them sequentially) so the landing screen isn't blocked on serial round-trips.
 * Each leaf call degrades to 0, so a partial outage yields a partial/zero
 * dashboard, never an error wall.
 *
 * COST: 44 requests per load (30 daily + 12 monthly + 2 prior windows). This
 * mirrors the ref production app, but it is heavy for the most-visible screen —
 * FLAGGED to Hector for a single bulk time-series endpoint (e.g.
 * `GET /searches/count/series/?granularity=day|month`) to collapse it to ~2 calls.
 */
export async function getScreeningCounts(now: Date = new Date()): Promise<ScreeningCounts> {
  const dailyWindows = Array.from({ length: 30 }, (_, idx) => {
    const from = addDays(now, -(30 - (idx + 1)));
    return { key: ymd(from), from: ymd(from), to: ymd(addDays(from, 1)) };
  });
  // Monthly windows anchor to the first of the month (see monthStart) so the
  // 12-month fan-out can't overflow on an end-of-month "now" (29th–31st).
  const monthBase = monthStart(now);
  const monthlyWindows = Array.from({ length: 12 }, (_, idx) => {
    const from = addMonths(monthBase, -(12 - (idx + 1)));
    return { key: ymd(from), from: ymd(from), to: ymd(addMonths(from, 1)) };
  });

  const [dailyCounts, monthlyCounts, prev30, prev12] = await Promise.all([
    Promise.all(dailyWindows.map((w) => getRequestsCount(w.from, w.to))),
    Promise.all(monthlyWindows.map((w) => getRequestsCount(w.from, w.to))),
    getRequestsCount(ymd(addDays(now, -60)), ymd(addDays(now, -30))),
    getRequestsCount(ymd(addMonths(monthBase, -24)), ymd(addMonths(monthBase, -12))),
  ]);

  const daily30 = dailyWindows.map((w, idx) => ({ key: w.key, count: dailyCounts[idx] }));
  const monthly12 = monthlyWindows.map((w, idx) => ({ key: w.key, count: monthlyCounts[idx] }));
  const total30d = dailyCounts.reduce((a, b) => a + b, 0);
  const total12m = monthlyCounts.reduce((a, b) => a + b, 0);

  return {
    daily30,
    monthly12,
    total30d,
    total12m,
    pct30d: percentageDifference(prev30, total30d),
    pct12m: percentageDifference(prev12, total12m),
  };
}
